/**
 * TheString: when LevelData.connectPlayersWithString is set, each new player is tied to the
 * previous one. The line stretches between them and turns red as it nears maxStringLength.
 * Past that length, or when either player is already dying, both die. Entering a door turns
 * that player's strings off.
 */
import { GameBehaviour, type Game, type GamePlayer } from '../game.ts';
import { PlayerController } from '../player.ts';
import { PrimaryWaysToDie, lerp, type RGBA, type V2 } from '../unity.ts';
import { num, rgba } from '../props.ts';

/** PlayerSpawner.stringPrefab on GlobalGameHandler (Assets/Prefabs/TheString.prefab). */
const STRING_PREFAB = '098d56fc20c9cd044bcb61a39dd324c3';

export class TheString extends GameBehaviour {
  player1: PlayerController | null = null;
  player2: PlayerController | null = null;
  isActive = true;
  maxDistance = 8;
  fromColor: RGBA = [0.34509805, 0.7529412, 0.6862745, 1];
  toColor: RGBA = [1, 0, 0, 1];

  override awake() {
    this.maxDistance = num(this.data.maxDistance, this.maxDistance);
    this.fromColor = rgba(this.data.fromColor, this.fromColor);
    this.toColor = rgba(this.data.toColor, this.toColor);
  }

  ties(a: PlayerController, b: PlayerController) {
    return (this.player1 === a && this.player2 === b) || (this.player1 === b && this.player2 === a);
  }

  tiesPlayer(p: GamePlayer) {
    return this.player1 === p || this.player2 === p;
  }

  setActive(active: boolean) {
    this.isActive = active;
    if (this.go.sprite) this.go.sprite.enabled = active;
  }

  override fixedUpdate() {
    if (!this.isActive || !this.player1 || !this.player2 || this.go.destroyed) return;
    const span = this.span();
    if (!span) return;
    if (span.dist > this.maxDistance || this.player1.isKindaDyingAlready() || this.player2.isKindaDyingAlready()) {
      const death = { primary: PrimaryWaysToDie.String, secondary: span.dist > this.maxDistance ? 'String' : 'StringPlayerDied' };
      this.player1.beginDeath(death);
      this.player2.beginDeath(death);
      if (this.go.sprite) this.go.sprite.color = [0, 0, 0, 0];
    } else this.layout(span);
  }

  /** Guests do not simulate. Draw the line on the players they are already showing. */
  present() {
    if (!this.player1 || !this.player2 || this.go.destroyed || !this.go.sprite) return;
    if (!this.isActive) {
      this.go.sprite.enabled = false;
      return;
    }
    this.go.sprite.enabled = true;
    const span = this.span();
    if (!span) return;
    if (span.dist > this.maxDistance || this.player1.isKindaDyingAlready() || this.player2.isKindaDyingAlready()) {
      this.go.sprite.color = [0, 0, 0, 0];
    } else this.layout(span);
  }

  private span(): { p1: V2; p2: V2; dx: number; dy: number; dist: number } | null {
    if (!this.player1 || !this.player2) return null;
    const p1 = anchor(this.player1);
    const p2 = anchor(this.player2);
    const dx = p2.x - p1.x;
    const dy = p2.y - p1.y;
    return { p1, p2, dx, dy, dist: Math.hypot(dx, dy) };
  }

  private layout(s: { p1: V2; p2: V2; dx: number; dy: number; dist: number }) {
    this.go.position = { x: (s.p1.x + s.p2.x) / 2, y: (s.p1.y + s.p2.y) / 2 };
    const rad = Math.atan2(s.dy, s.dx);
    const c = Math.cos(rad);
    const sn = Math.sin(rad);
    const m = this.go.local;
    this.go.setLocal({ a: c, b: sn, c: -sn, d: c, tx: m.tx, ty: m.ty });
    const sr = this.go.sprite;
    if (!sr) return;
    const tNorm = this.maxDistance > 0 ? Math.min(1, s.dist / this.maxDistance) : 1;
    const fade = tNorm <= 0.5 ? 0 : (tNorm - 0.5) / 0.5;
    sr.color = [
      lerp(this.fromColor[0], this.toColor[0], fade),
      lerp(this.fromColor[1], this.toColor[1], fade),
      lerp(this.fromColor[2], this.toColor[2], fade),
      lerp(this.fromColor[3], this.toColor[3], fade),
    ];
    sr.size = [s.dist, sr.size[1]];
  }
}

function anchor(p: PlayerController): V2 {
  const q = p.go.position;
  return p.isCrouching ? { x: q.x, y: q.y - 0.25 } : { x: q.x, y: q.y };
}

/** PlayerSpawner.SpawnPlayerObject: one string from the previous player to the new one. */
export function ensurePlayerStrings(game: Game) {
  if (!game.levelMeta.connectPlayersWithString) return;
  const players = game.players.filter((p): p is PlayerController => p instanceof PlayerController && !p.go.destroyed);
  for (let i = 1; i < players.length; i++) {
    const a = players[i - 1];
    const b = players[i];
    if (game.behaviours.some((s) => s instanceof TheString && !s.go.destroyed && s.ties(a, b))) continue;
    const go = game.instantiate(STRING_PREFAB, a.go.position);
    const s = go?.getBehaviour(TheString);
    if (!go || !s) continue;
    s.player1 = a;
    s.player2 = b;
    s.maxDistance = game.levelMeta.maxStringLength;
    s.present();
    if (game.frame > 0) s.sound('stringTie', b.go.position);
  }
}

export function cutAllStrings(game: Game) {
  let cut: TheString | null = null;
  for (const s of game.behaviours) {
    if (!(s instanceof TheString) || s.go.destroyed) continue;
    if (s.isActive) cut ??= s;
    s.setActive(false);
  }
  cut?.sound('stringTie', cut.player1?.go.position);
}

export function deactivatePlayerStrings(game: Game, player: GamePlayer) {
  for (const s of game.behaviours) {
    if (s instanceof TheString && !s.go.destroyed && s.tiesPlayer(player)) s.setActive(false);
  }
}

export function presentPlayerStrings(game: Game) {
  for (const s of game.behaviours) if (s instanceof TheString && !s.go.destroyed) s.present();
}

export function applyStringLinks(game: Game, links: { a: number; b: number; on: boolean }[] | undefined) {
  if (!links) return;
  for (const link of links) {
    const a = game.players[link.a];
    const b = game.players[link.b];
    if (!(a instanceof PlayerController) || !(b instanceof PlayerController)) continue;
    for (const s of game.behaviours) {
      if (s instanceof TheString && !s.go.destroyed && s.ties(a, b)) s.setActive(link.on);
    }
  }
}
